/**
 * Ein Ort für alles, was die App zu melden hat.
 *
 * Vorher standen 179 `console.*`-Aufrufe im Code, und drei Dinge waren daran
 * falsch:
 *
 * 1. **Am Spieltisch sieht sie niemand.** Die Spieler halten Handys in der
 *    Hand. Wenn dort etwas nicht lädt, ist die Browserkonsole unerreichbar,
 *    und die Antwort auf »was stand da?« lautet immer »weiß ich nicht«.
 *    Deshalb landet jede Meldung zusätzlich in einem Ringspeicher, den die
 *    ErrorBoundary ausklappt.
 *
 * 2. **Vier Aufrufe schoben ganze Objekte hinaus** – `DEBUG: Character Data`
 *    im Inventar, `Geladene Charaktere (Cache)` in der Heldenauswahl. Das war
 *    beim Suchen einmal nützlich und ist seither nur noch Lärm. `alsText()`
 *    kürzt bei OBERGRENZE_ZEICHEN, damit das nicht wiederkommt.
 *
 * 3. **In Tests rauschte die Ausgabe voll** mit Fehlern, die der Test genau
 *    so erwartet. Dazwischen einen echten zu finden, ist Glückssache.
 *
 * Absichtlich kein Dienst im Netz. Was hier durchläuft, sind Spielernamen,
 * Heldendaten und Mailadressen einer privaten Runde. Die haben auf keinem
 * fremden Server etwas zu suchen. Der Ringspeicher lebt im Browser und ist
 * beim Neuladen weg – das reicht, um am Tisch nachzusehen, was gerade schieflief.
 */

export type Stufe = 'fehler' | 'warnung' | 'hinweis';

export type Eintrag = {
  zeit: string;
  stufe: Stufe;
  bereich: string;
  meldung: string;
  details: string | null;
};

/** So viele Meldungen hält der Ringspeicher. Danach fällt die älteste heraus. */
export const MAX_EINTRAEGE = 50;

/** Länger wird kein Detail. Siehe Punkt 2 oben. */
export const OBERGRENZE_ZEICHEN = 500;

const kuerzen = (text: string): string =>
  text.length > OBERGRENZE_ZEICHEN ? `${text.slice(0, OBERGRENZE_ZEICHEN)}…` : text;

/**
 * Macht aus dem zweiten Argument eines alten `console.error(…, x)` eine Zeile,
 * die man lesen kann – ohne zu werfen, egal was hereinkommt.
 */
export const alsText = (details: unknown): string | null => {
  if (details === null || details === undefined) return null;
  if (typeof details === 'string') return kuerzen(details);
  if (details instanceof Error) return kuerzen(`${details.name}: ${details.message}`);

  if (typeof details === 'object') {
    const objekt = details as Record<string, unknown>;
    // Der Regelfall: ein Fehler aus @supabase/supabase-js.
    if ('message' in objekt && objekt.message !== null && objekt.message !== undefined) {
      const meldung = String(objekt.message);
      const code = objekt.code === null || objekt.code === undefined ? '' : String(objekt.code);
      return kuerzen(code ? `${meldung} (${code})` : meldung);
    }
    try {
      return kuerzen(JSON.stringify(details));
    } catch {
      // Ein Ring im Objekt. Der Reihe nach, was geht.
      const felder = Object.entries(objekt)
        .map(
          ([schluessel, wert]) => `${schluessel}=${typeof wert === 'object' ? '…' : String(wert)}`
        )
        .join(', ');
      return kuerzen(`{ ${felder} }`);
    }
  }

  return kuerzen(String(details));
};

/**
 * Entscheidet, was in welchem Modus auf der Konsole erscheint.
 *
 * Unbekannter Modus gilt als Produktion: Wer nicht sicher weiß, dass er
 * entwickelt, soll nicht die gesprächige Fassung bekommen.
 */
export const gehtAufDieKonsole = (stufe: Stufe, modus: string): boolean => {
  if (modus === 'test') return false;
  if (modus === 'development') return true;
  return stufe === 'fehler';
};

const speicher: Eintrag[] = [];

const aktuellerModus = (): string => {
  try {
    return import.meta.env?.MODE ?? 'production';
  } catch {
    return 'production';
  }
};

const konsole: Record<Stufe, (...args: unknown[]) => void> = {
  fehler: (...args) => console.error(...args),
  warnung: (...args) => console.warn(...args),
  hinweis: (...args) => console.info(...args),
};

const schreiben = (
  stufe: Stufe,
  bereich: string,
  meldung: string,
  details?: unknown,
  modus?: string
) => {
  const text = alsText(details);

  speicher.push({ zeit: new Date().toISOString(), stufe, bereich, meldung, details: text });
  if (speicher.length > MAX_EINTRAEGE) speicher.shift();

  if (!gehtAufDieKonsole(stufe, modus ?? aktuellerModus())) return;
  const kopf = `[${bereich}] ${meldung}`;
  if (text === null) konsole[stufe](kopf);
  else konsole[stufe](kopf, text);
};

export const protokoll = {
  /** Etwas ist schiefgegangen und der Nutzer merkt es. */
  fehler: (bereich: string, meldung: string, details?: unknown, modus?: string) =>
    schreiben('fehler', bereich, meldung, details, modus),
  /** Etwas ist schiefgegangen, die App arbeitet aber weiter. */
  warnung: (bereich: string, meldung: string, details?: unknown, modus?: string) =>
    schreiben('warnung', bereich, meldung, details, modus),
  /** Erwartbar, nur gut zu wissen. */
  hinweis: (bereich: string, meldung: string, details?: unknown, modus?: string) =>
    schreiben('hinweis', bereich, meldung, details, modus),
};

/** Die jüngsten Meldungen zuerst. Immer eine Kopie – niemand soll den Speicher umbauen. */
export const eintraege = (): Eintrag[] => [...speicher].reverse();

export const leeren = (): void => {
  speicher.length = 0;
};
